import React from 'react';

/**
 * Componente de botão reutilizável
 * @param {object} props - Propriedades do componente
 * @param {string} [props.variant='primary'] - Variante do botão (primary, secondary, success, outline, text, subtle, link)
 * @param {string} [props.size='md'] - Tamanho do botão (xs, sm, md, lg)
 * @param {boolean} [props.fullWidth=false] - Se o botão deve ocupar largura total
 * @param {boolean} [props.disabled=false] - Se o botão está desabilitado
 * @param {boolean} [props.loading=false] - Se o botão está em estado de carregamento
 * @param {React.ReactNode} props.children - Conteúdo do botão
 * @param {function} [props.onClick] - Função de clique
 * @param {string} [props.className] - Classes adicionais
 * @param {string} [props.type='button'] - Tipo do botão (button, submit, reset)
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  isLoading, // Para compatibilidade com componentes que usam isLoading em vez de loading
  children,
  onClick,
  className = '',
  type = 'button',
  ...props
}) => {
  // Verificar se loading está ativo (aceita tanto loading quanto isLoading)
  const isLoadingState = loading || isLoading;
  
  // Variantes de estilo
  const variantStyles = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500',
    secondary: 'bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500',
    success: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500',
    outline: 'border border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500',
    text: 'text-blue-600 hover:bg-blue-50 focus:ring-blue-500',
    subtle: 'bg-blue-50 text-blue-600 hover:bg-blue-100 focus:ring-blue-400',
    link: 'text-blue-600 hover:text-blue-800 focus:underline bg-transparent'
  };

  // Tamanhos
  const sizeStyles = {
    xs: 'px-3 py-1 text-xs',
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg'
  };
  
  // Ajustar padding para variante link (apenas padding horizontal mínimo)
  const getLinkSizeStyle = (size) => {
    switch(size) {
      case 'xs': return 'px-1 py-0.5 text-xs';
      case 'sm': return 'px-1 py-0.5 text-sm';
      case 'md': return 'px-1.5 py-0.5 text-base';
      case 'lg': return 'px-1.5 py-0.5 text-lg font-medium';
      default: return 'px-1 py-0.5 text-sm';
    }
  };

  // Classes base
  const baseClasses = 'font-medium rounded focus:outline-none transition-colors';
  
  // Classes adicionais baseadas no tipo
  const ringClasses = variant === 'link' ? '' : 
                     (variant !== 'subtle' ? 'focus:ring-2 focus:ring-offset-2' : 'focus:ring-1');
  
  // Determinar o estilo de tamanho a ser usado
  const sizeStyle = variant === 'link' ? getLinkSizeStyle(size) : sizeStyles[size];
  
  // Classes combinadas
  const combinedClasses = `
    ${baseClasses}
    ${ringClasses}
    ${variantStyles[variant]}
    ${sizeStyle}
    ${fullWidth ? 'w-full' : ''}
    ${disabled || isLoadingState ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
    ${className}
  `.trim();

  // Remover isLoading de props para não passá-la para o elemento DOM
  const { isLoading: _, ...restProps } = props;

  return (
    <button
      type={type}
      className={combinedClasses}
      onClick={onClick}
      disabled={disabled || isLoadingState}
      {...restProps}
    >
      {isLoadingState ? (
        <div className="flex items-center justify-center">
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {children}
        </div>
      ) : children}
    </button>
  );
};

export default Button; 